import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, RefreshCw } from 'lucide-react';
import { kbSubscriptionService, type KBSubscription, type KBCatalogEntry } from '../services/kbSubscriptionService';
import { clientService, type Client } from '../services/clientService';
import { useAuth } from '../hooks/useAuth';
import PageHeader from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';

const fmt = (d: string) => new Date(d).toLocaleDateString('en-GB');

export default function SettingsSubscriptions() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'SysAdmin' || user?.role === 'DevOps';

  const [subs, setSubs] = useState<KBSubscription[]>([]);
  const [catalog, setCatalog] = useState<KBCatalogEntry[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ client_id: '', kb_id: '', notes: '' });

  const load = async () => {
    try {
      setLoading(true); setError(null);
      const subsData = await kbSubscriptionService.getAll();
      const catalogData = await kbSubscriptionService.getCatalog();
      setSubs(subsData); setCatalog(catalogData);
      if (isAdmin) {
        const clientsData = await clientService.getAll();
        setClients(clientsData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('pages.settingsSubscriptions.errorLoad'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.client_id || !form.kb_id) return;
    try {
      setCreating(true);
      await kbSubscriptionService.create({ client_id: form.client_id, kb_id: form.kb_id, notes: form.notes || undefined });
      setDialogOpen(false); setForm({ client_id: '', kb_id: '', notes: '' });
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('pages.settingsSubscriptions.errorCreate'));
    } finally { setCreating(false); }
  };

  const handleRevoke = async (sub: KBSubscription) => {
    if (!window.confirm(t('pages.settingsSubscriptions.revokeConfirm', { kb: sub.kb_name, client: sub.client_name }))) return;
    try {
      await kbSubscriptionService.revoke(sub.id);
      await load();
    } catch (err) { alert(err instanceof Error ? err.message : t('pages.settingsSubscriptions.errorRevoke')); }
  };

  const sharedKBs = catalog.filter(k => k.visibility === 'shared');

  if (loading) return (
    <>
      <PageHeader title={t('pages.settingsSubscriptions.title')} />
      <div className="px-6 py-4 space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
    </>
  );

  if (error) return (
    <>
      <PageHeader title={t('pages.settingsSubscriptions.title')} />
      <div className="px-6 py-4 text-sm text-red-600">{error}</div>
    </>
  );

  return (
    <>
      <PageHeader
        title={t('pages.settingsSubscriptions.title')}
        subtitle={t('pages.settingsSubscriptions.subtitle')}
        actions={isAdmin ? (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={load}><RefreshCw size={13} className="mr-1"/>{t('common.refresh')}</Button>
            <Button size="sm" onClick={() => setDialogOpen(true)}><Plus size={13} className="mr-1"/>{t('pages.settingsSubscriptions.grant')}</Button>
          </div>
        ) : undefined}
      />

      <div className="px-6 pb-6 space-y-4 pt-4">
        {subs.length === 0 ? (
          <p className="text-sm text-slate-500">{t('pages.settingsSubscriptions.empty')}</p>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-slate-200 text-xs text-slate-500 uppercase tracking-wide">
                <th className="text-left py-2 pr-4 font-medium">{t('pages.settingsSubscriptions.colKB')}</th>
                {isAdmin && <th className="text-left py-2 pr-4 font-medium">{t('pages.settingsSubscriptions.colClient')}</th>}
                <th className="text-left py-2 pr-4 font-medium">{t('pages.settingsSubscriptions.colGranted')}</th>
                <th className="text-left py-2 pr-4 font-medium">{t('pages.settingsSubscriptions.colGrantedBy')}</th>
                <th className="text-left py-2 pr-4 font-medium">{t('pages.settingsSubscriptions.colNotes')}</th>
                {isAdmin && <th className="w-8"/>}
              </tr>
            </thead>
            <tbody>
              {subs.map(sub => (
                <tr key={sub.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 pr-4 font-medium text-slate-800">{sub.kb_name || sub.kb_id}</td>
                  {isAdmin && <td className="py-2 pr-4 text-slate-600">{sub.client_name || sub.client_id}</td>}
                  <td className="py-2 pr-4 text-slate-500">{fmt(sub.granted_at)}</td>
                  <td className="py-2 pr-4 text-slate-500 truncate max-w-[120px]">{sub.granted_by}</td>
                  <td className="py-2 pr-4 text-slate-400 text-xs truncate max-w-[160px]">{sub.notes || '—'}</td>
                  {isAdmin && (
                    <td className="py-2">
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleRevoke(sub)}>
                        <Trash2 size={12}/>
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {isAdmin && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>{t('pages.settingsSubscriptions.grantTitle')}</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-3 pt-1">
              <div className="space-y-1">
                <Label>{t('pages.settingsSubscriptions.client')} *</Label>
                <Select value={form.client_id || 'none'} onValueChange={v => setForm(f => ({ ...f, client_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={t('pages.settingsSubscriptions.selectClient')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('pages.settingsSubscriptions.selectClient')}</SelectItem>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>{t('pages.settingsSubscriptions.kb')} *</Label>
                <Select value={form.kb_id || 'none'} onValueChange={v => setForm(f => ({ ...f, kb_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={t('pages.settingsSubscriptions.selectKB')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('pages.settingsSubscriptions.selectKB')}</SelectItem>
                    {sharedKBs.map(k => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>{t('pages.settingsSubscriptions.notes')}</Label>
                <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="resize-none text-xs" placeholder={t('pages.settingsSubscriptions.notesPlaceholder')} />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
                <Button type="submit" disabled={creating || !form.client_id || !form.kb_id}>{creating ? t('common.saving') : t('pages.settingsSubscriptions.grant')}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
